import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Shield, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type Tab = 'terms' | 'privacy' | 'disclaimer';

const LegalPage: React.FC = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<Tab>('terms');

    const tabs = [
        { id: 'terms' as Tab, label: t('legal.tabs.terms'), icon: FileText },
        { id: 'privacy' as Tab, label: t('legal.tabs.privacy'), icon: Shield },
        { id: 'disclaimer' as Tab, label: t('legal.tabs.disclaimer'), icon: AlertTriangle }
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="p-6 lg:p-10 max-w-5xl mx-auto"
        >
            <div className="mb-8">
                <h1 className="text-4xl font-black leading-tight tracking-[-0.033em] text-transparent bg-clip-text bg-gradient-primary mb-2">
                    {t('legal.page_title')}
                </h1>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                    {t('legal.last_updated')}
                </p>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
                {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-3 font-semibold transition-colors relative ${activeTab === tab.id
                                ? 'text-primary'
                                : 'text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary'
                                }`}
                        >
                            <Icon className="size-5" />
                            <span className="hidden sm:inline">{tab.label}</span>
                            {activeTab === tab.id && (
                                <motion.div
                                    layoutId="activeTab"
                                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                                />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Content */}
            <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="card p-8 prose prose-gray dark:prose-invert max-w-none"
            >
                {activeTab === 'terms' && <TermsContent />}
                {activeTab === 'privacy' && <PrivacyContent />}
                {activeTab === 'disclaimer' && <DisclaimerContent />}
            </motion.div>
        </motion.div>
    );
};

const TermsContent: React.FC = () => {
    const { t } = useTranslation();

    return (
        <div className="text-gray-800 dark:text-gray-200">
            <h2 className="text-2xl font-bold mb-4 text-[#181411] dark:text-white">{t('legal.terms.title')}</h2>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.terms.section_1_title')}</h3>
                <p className="mb-3">{t('legal.terms.section_1_content')}</p>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.terms.section_2_title')}</h3>
                <p className="mb-3">{t('legal.terms.section_2_intro')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.terms.section_2_items', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.terms.section_3_title')}</h3>
                <p className="mb-3">{t('legal.terms.section_3_content')}</p>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.terms.section_4_title')}</h3>
                <p className="mb-3">{t('legal.terms.section_4_intro')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.terms.section_4_items', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.terms.section_5_title')}</h3>
                <p className="mb-3">{t('legal.terms.section_5_content')}</p>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.terms.section_6_title')}</h3>
                <p className="mb-3">{t('legal.terms.section_6_content')}</p>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.terms.section_7_title')}</h3>
                <p className="mb-3">{t('legal.terms.section_7_content')}</p>
            </section>
        </div>
    );
};

const PrivacyContent: React.FC = () => {
    const { t } = useTranslation();

    return (
        <div className="text-gray-800 dark:text-gray-200">
            <h2 className="text-2xl font-bold mb-4 text-[#181411] dark:text-white">{t('legal.privacy.title')}</h2>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.privacy.section_1_title')}</h3>
                <h4 className="font-semibold mb-2">{t('legal.privacy.section_1_subtitle_1')}</h4>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.privacy.section_1_items_1', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
                <h4 className="font-semibold mb-2">{t('legal.privacy.section_1_subtitle_2')}</h4>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.privacy.section_1_items_2', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.privacy.section_2_title')}</h3>
                <p className="mb-3">{t('legal.privacy.section_2_intro')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.privacy.section_2_items', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.privacy.section_3_title')}</h3>
                <p className="mb-3 font-semibold">{t('legal.privacy.section_3_warning')}</p>
                <p className="mb-3">{t('legal.privacy.section_3_intro')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.privacy.section_3_items', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.privacy.section_4_title')}</h3>
                <p className="mb-3">{t('legal.privacy.section_4_intro')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.privacy.section_4_items', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.privacy.section_5_title')}</h3>
                <p className="mb-3">{t('legal.privacy.section_5_intro')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.privacy.section_5_items', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.privacy.section_6_title')}</h3>
                <p className="mb-3">{t('legal.privacy.section_6_intro')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.privacy.section_6_items', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>
        </div>
    );
};

const DisclaimerContent: React.FC = () => {
    const { t } = useTranslation();

    return (
        <div className="text-gray-800 dark:text-gray-200">
            <h2 className="text-2xl font-bold mb-4 text-[#181411] dark:text-white">{t('legal.disclaimer.title')}</h2>

            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-4 mb-6">
                <p className="font-semibold text-yellow-800 dark:text-yellow-200">
                    {t('legal.disclaimer.warning')}
                </p>
            </div>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.disclaimer.section_1_title')}</h3>
                <div className="space-y-4">
                    <div className="border-l-4 border-red-500 pl-4">
                        <h4 className="font-semibold text-red-600 dark:text-red-400">{t('legal.disclaimer.no_financial_return_title')}</h4>
                        <p className="text-sm">{t('legal.disclaimer.no_financial_return_content')}</p>
                    </div>
                    <div className="border-l-4 border-red-500 pl-4">
                        <h4 className="font-semibold text-red-600 dark:text-red-400">{t('legal.disclaimer.no_lead_quantity_title')}</h4>
                        <p className="text-sm">{t('legal.disclaimer.no_lead_quantity_content')}</p>
                    </div>
                    <div className="border-l-4 border-red-500 pl-4">
                        <h4 className="font-semibold text-red-600 dark:text-red-400">{t('legal.disclaimer.no_lead_quality_title')}</h4>
                        <p className="text-sm">{t('legal.disclaimer.no_lead_quality_content')}</p>
                    </div>
                    <div className="border-l-4 border-red-500 pl-4">
                        <h4 className="font-semibold text-red-600 dark:text-red-400">{t('legal.disclaimer.no_conversion_rate_title')}</h4>
                        <p className="text-sm">{t('legal.disclaimer.no_conversion_rate_content')}</p>
                    </div>
                    <div className="border-l-4 border-red-500 pl-4">
                        <h4 className="font-semibold text-red-600 dark:text-red-400">{t('legal.disclaimer.no_contracts_title')}</h4>
                        <p className="text-sm">{t('legal.disclaimer.no_contracts_content')}</p>
                    </div>
                </div>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.disclaimer.section_2_title')}</h3>
                <p className="mb-3">{t('legal.disclaimer.section_2_intro')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.disclaimer.section_2_services', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}><strong>{item.split(':')[0]}:</strong>{item.split(':')[1]}</li>
                    ))}
                </ul>
                <p className="font-semibold text-red-600 dark:text-red-400 mb-2">{t('legal.disclaimer.section_2_not_responsible')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.disclaimer.section_2_items', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.disclaimer.section_3_title')}</h3>
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded p-4">
                    <p className="font-semibold mb-2 text-orange-800 dark:text-orange-200">{t('legal.disclaimer.section_3_warning')}</p>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                        {(t('legal.disclaimer.section_3_items', { returnObjects: true }) as string[]).map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>
                </div>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.disclaimer.section_4_title')}</h3>
                <p className="mb-3">{t('legal.disclaimer.section_4_intro')}</p>
                <ul className="list-disc pl-6 mb-3">
                    {(t('legal.disclaimer.section_4_items', { returnObjects: true }) as string[]).map((item, i) => (
                        <li key={i}>{item}</li>
                    ))}
                </ul>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.disclaimer.section_5_title')}</h3>
                <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded">
                    <p className="font-semibold mb-2">{t('legal.disclaimer.section_5_intro')}</p>
                    <ul className="list-disc pl-6 text-sm">
                        {(t('legal.disclaimer.section_5_items', { returnObjects: true }) as string[]).map((item, i) => (
                            <li key={i}>{item}</li>
                        ))}
                    </ul>
                    <p className="mt-3 font-semibold">{t('legal.disclaimer.section_5_limit')}</p>
                </div>
            </section>

            <section className="mb-6">
                <h3 className="text-xl font-semibold mb-3 text-[#181411] dark:text-white">{t('legal.disclaimer.section_6_title')}</h3>
                <p className="mb-3">{t('legal.disclaimer.section_6_content')}</p>
                <p className="font-semibold text-red-600 dark:text-red-400">{t('legal.disclaimer.section_6_warning')}</p>
            </section>
        </div>
    );
};

export default LegalPage;
